'use client'
import { useEffect, useRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

export default function VisionSection() {
  const t = useTranslations('About');

  const titleRef   = useRef<HTMLHeadingElement>(null);
  const gridRef    = useRef<HTMLDivElement>(null);

  const services = [
    { number: '01', title: t('service_1_title'), description: t('service_1_desc') },
    { number: '02', title: t('service_2_title'), description: t('service_2_desc') },
    { number: '03', title: t('service_3_title'), description: t('service_3_desc') },
    { number: '04', title: t('service_4_title'), description: t('service_4_desc') },
  ];

  useEffect(() => {
    let cancelled = false;
    let ctx: any;
    Promise.all([import("gsap"), import("gsap/ScrollTrigger")]).then(
      ([{ gsap }, { ScrollTrigger }]) => {
      if (cancelled) return;
        gsap.registerPlugin(ScrollTrigger);

        gsap.from(titleRef.current, {
          opacity: 0,
          x: 70,
          duration: 0.95,
          ease: "power3.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top 75%",
          },
        });

        gsap.from(
          gridRef.current ? Array.from(gridRef.current.children) : [],
          {
            opacity: 0,
            y: 55,
            duration: 0.75,
            stagger: 0.15,
            ease: "power3.out",
            scrollTrigger: {
              trigger: gridRef.current,
              start: "top 80%",
            },
          }
        );
      }
    );
    return () => { cancelled = true; ctx?.revert(); };
  }, []);

  return (
    <section className="relative w-full min-h-screen bg-[#111] flex flex-col justify-between p-6 md:p-12 lg:p-16 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <Image
          src="/images/vision_bg.jpg"
          alt="Modern architecture with rusted corten steel facade"
          fill
          priority
          sizes="100vw"
          className="object-cover object-bottom brightness-[0.85] contrast-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-8xl mx-auto flex justify-end pt-12 md:pt-16">
        <h1
          ref={titleRef}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-white tracking-tight text-right max-w-2xl leading-[1.15]"
        >
          {t('vision_title')}
        </h1>
      </div>

      <div className="relative z-10 w-full max-w-8xl mx-auto pt-20 pb-4">
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-4 md:gap-x-6">
          {services.map((service, i) => (
            <div key={i} className="relative pl-4 md:pl-6 py-4 flex flex-col justify-start min-h-[140px]">
              <span className="block text-2xl md:text-4xl lg:text-6xl font-semibold text-white/90 tracking-wide">
                {service.number}
              </span>
              <h3 className="text-xl md:text-2xl lg:text-4xl font-normal text-white mt-1 mb-3">
                {service.title}
              </h3>
              <p className="text-gray-300 text-xs md:text-sm leading-relaxed max-w-[220px]">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
